export const TRAINING_VIDEO_SRC = "/training/how-to-create-a-retreat.mp4";

const sharedVideo = {
  videoSrc: "/training/guide-placeholder.mp4",
  durationSeconds: 200,
};

export const trainingGuides = [
  {
    id: "create-edit-retreat",
    title: "How to Create / Edit a Retreat",
    description:
      "Close the guide, click Add Retreat, and fill in the parish, dates, details, payment, and team. The registration page updates on the right. Click a retreat title later to edit it.",
    topic: "Retreats",
    lengthLabel: "05:37",
    featured: true,
    videoSrc: TRAINING_VIDEO_SRC,
    thumbnail: "/training/how-to-create-a-retreat.png",
    durationSeconds: 337,
    narrated: true,
    steps: [
      {
        id: "welcome",
        title: "Close the Guide",
        time: 0,
        timeLabel: "00:00",
        body: "Click Close at the bottom right to leave this popup. You are now on the Manage Retreats page.",
        control: "Close",
        page: "/manageRetreat",
      },
      {
        id: "create",
        title: "Start a New Retreat",
        time: 10,
        timeLabel: "00:10",
        body: "Click the red Add Retreat button in the top right. The creation screen opens, with five steps on the left and a live preview of the registration page on the right.",
        control: "Add Retreat",
        page: "/create-retreat",
        section: "parish",
        action: {
          label: "Open retreat form",
          to: "/create-retreat",
          continueGuide: true,
        },
      },
      {
        id: "full-form",
        title: "Open Full Form",
        time: 25,
        timeLabel: "00:25",
        body: "Click Full Form at the top right to see every section in one window. Close that popup and continue with the step-by-step builder.",
        control: "Full Form",
        page: "/create-retreat",
        section: "parish",
      },
      {
        id: "title",
        title: "Parish, Language and Title",
        time: 40,
        timeLabel: "00:40",
        body: "Open step 1, Retreat Title. Choose the church in Select Parish, then set Language to English. The location, address, and retreat title fill in and appear on the live preview.",
        control: "Select Parish, Language",
        page: "/create-retreat",
        section: "parish",
      },
      {
        id: "image",
        title: "Image and Retreat Theme",
        time: 58,
        timeLabel: "00:58",
        body: "Click the orange Upload Retreat Image button, choose a photo of the church, and click Open. The photo, title, and address show on the preview card. Then type the weekend theme in Retreat Theme. A yellow banner updates as you type.",
        control: "Upload Retreat Image, Retreat Theme",
        page: "/create-retreat",
        section: "parish",
      },
      {
        id: "dates",
        title: "Set the Retreat Dates",
        time: 89,
        timeLabel: "01:29",
        body: "Open step 2, Retreat Dates, and fold step 1. Choose the Friday, Saturday, and Sunday dates for this retreat, and set each day's start and end time. A green message confirms the retreat is saved automatically.",
        control: "Retreat Dates",
        page: "/create-retreat",
        section: "weekend",
      },
      {
        id: "weekend-times",
        title: "Saturday, Sunday, and the Schedule",
        time: 121,
        timeLabel: "02:01",
        body: "Set Saturday and Sunday From and To times for this retreat. The Retreat Schedule on the right lists those dates and times.",
        control: "From, To",
        page: "/create-retreat",
        section: "weekend",
      },
      {
        id: "details",
        title: "Open Retreat Details",
        time: 145,
        timeLabel: "02:25",
        body: "Open step 3, Retreat Details. The mission statement is already filled in, and you can edit it. The description box is underneath.",
        control: "Retreat Details",
        page: "/create-retreat",
        section: "story",
      },
      {
        id: "description",
        title: "Paste the Description",
        time: 160,
        timeLabel: "02:40",
        body: "Click Description Sample, copy the template, and click Close. Paste it into the description box. The text shows on the registration page on the right.",
        control: "Description Sample",
        page: "/create-retreat",
        section: "story",
      },
      {
        id: "email",
        title: "Preview the Couple Email",
        time: 172,
        timeLabel: "02:52",
        body: "Scroll to the email message. Click Sample Email Message, copy the template, close the popup, and paste it into the email message box. Click Preview Email to see what couples receive after they register, then click Close.",
        control: "Sample Email Message, Preview Email",
        page: "/create-retreat",
        section: "story",
      },
      {
        id: "payment",
        title: "Add the Payment Link",
        time: 193,
        timeLabel: "03:13",
        body: "Open step 4, Payment. Click Retreat Payment Link and type or paste the payment website so couples can pay the registration fee.",
        control: "Retreat Payment Link",
        page: "/create-retreat",
        section: "payment",
      },
      {
        id: "leaders",
        title: "Assign the Retreat Team",
        time: 207,
        timeLabel: "03:27",
        body: "Open step 5, Team, and click the plus next to Retreat Team. In Add Retreat Team, choose Lead, pick the couple, and click the orange plus. Add the co-lead the same way. Choose Mentor, select that couple, and click plus. Choose Spiritual Director, select the priest, and click plus. Click Close when the team is listed.",
        control: "Retreat Team",
        page: "/create-retreat",
        section: "team",
      },
      {
        id: "admins",
        title: "Retreat Administrators",
        time: 253,
        timeLabel: "04:13",
        body: "Retreat Administrators fills in with the team you just added. Click the plus there if you need to add another administrator.",
        control: "Retreat Administrators",
        page: "/create-retreat",
        section: "team",
      },
      {
        id: "leadership",
        title: "Review Leadership on the Preview",
        time: 270,
        timeLabel: "04:30",
        body: "Under Retreat Leadership, the lead, co-lead, and mentor photos and names are shown, with options to remove a couple or a photo. Scroll the preview for the schedule, the details, and the registration form.",
        control: "Retreat Leadership",
        page: "/create-retreat",
        section: "team",
      },
      {
        id: "preview",
        title: "Open the Public Registration Page",
        time: 295,
        timeLabel: "04:55",
        body: "Everything on the left is complete. Click Preview at the top right. A new tab opens with the live page couples use to register. Switch back to the admin tab and click Back at the top left.",
        control: "Preview, Back",
        page: "/create-retreat",
        section: "save",
      },
      {
        id: "edit-existing",
        title: "Find the Retreat and Edit It",
        time: 319,
        timeLabel: "05:19",
        body: "Close this guide to return to Retreats. The new retreat is at the top of the table. To change a retreat that already exists, click its title in the list and edit the details.",
        control: "Retreat title",
        page: "/manageRetreat",
        action: {
          label: "Back to Retreats",
          to: "/manageRetreat",
        },
      },
    ],
  },
  {
    id: "retreat-roster",
    title: "How to Use the Retreat Roster",
    description:
      "Open a retreat roster, review missing data, send the directory and program, build the team, assign talks, and add or edit couples.",
    topic: "Retreat Roster",
    lengthLabel: "09:13",
    videoSrc: "/training/how-to-use-the-retreat-roster.mp4",
    thumbnail: "/training/how-to-use-the-retreat-roster.png",
    durationSeconds: 553,
    narrated: true,
    steps: [
      {
        id: "open-roster",
        title: "Open the Roster",
        time: 0,
        timeLabel: "00:00",
        body: "Close this guide when you want to work on the page. On Retreats, click a retreat title. That weekend's roster opens.",
        control: "Retreat title",
        page: "/retreatRoster",
      },
      {
        id: "header",
        title: "Review the Retreat Header",
        time: 18,
        timeLabel: "00:18",
        body: "The header shows the lead couple, parish, dates, Retreat ID, and Parish ID. The color dots filter Lead Couple, Spiritual Mentor, Retreatant, and Team.",
        control: "Lead Couple, Spiritual Mentor, Retreatant, Team",
        page: "/retreatRoster",
      },
      {
        id: "role-filters",
        title: "Filter by Role",
        time: 35,
        timeLabel: "00:35",
        body: "Click a count chip such as Spiritual Mentor, Team, Retreatant, Services, Talks, or RTP to show only that group. Clear filter brings the full roster back.",
        control: "Role chips, Clear filter",
        page: "/retreatRoster",
      },
      {
        id: "missing-data",
        title: "Check Missing Data",
        time: 52,
        timeLabel: "00:52",
        body: "Click Missing Data, to the left of Search roster. It lists couples who still need an email, phone, Picture, or another required roster field. Yellow cells show ⚠ Missing.",
        control: "Missing Data",
        page: "/retreatRoster",
      },
      {
        id: "send-directory",
        title: "Send the Directory",
        time: 68,
        timeLabel: "01:08",
        body: "Click Send Directory. Choose retreatants, team couples, or both, then send. The button shows how many people will receive it.",
        control: "Send Directory",
        page: "/retreatRoster",
      },
      {
        id: "send-program",
        title: "Send the Program",
        time: 100,
        timeLabel: "01:40",
        body: "Click Send Program and choose the same way. Pick the retreatants and team couples who should receive the weekend program, then send.",
        control: "Send Program",
        page: "/retreatRoster",
      },
      {
        id: "checklist",
        title: "Use the Closing Checklist",
        time: 118,
        timeLabel: "01:58",
        body: "Open the closing checklist. Confirm Roster Data, Directory, Program, and Pictures. The retreat can be validated and closed after all four are confirmed.",
        control: "Closing Checklist",
        page: "/retreatRoster",
      },
      {
        id: "registration",
        title: "Open the Registration Page",
        time: 128,
        timeLabel: "02:08",
        body: "Click the retreat dates or the parish name. The public registration page opens, with the church photo, the mission, and retreat leadership.",
        control: "Retreat dates",
        page: "/retreatRoster",
      },
      {
        id: "build-team",
        title: "Build My Team",
        time: 150,
        timeLabel: "02:30",
        body: "Click Build My Team. Filter by language, parish, and how couples have already served, such as couples who have never teamed from your parish. Export that list to Excel or PDF.",
        control: "Build My Team",
        page: "/retreatRoster",
      },
      {
        id: "admins",
        title: "Add a Retreat Admin",
        time: 205,
        timeLabel: "03:25",
        body: "Click Add Retreat Admin. Search for a couple, click the plus, and close the window. That couple can help manage this retreat.",
        control: "Add Retreat Admin",
        page: "/retreatRoster",
      },
      {
        id: "talks",
        title: "Assign Talks",
        time: 230,
        timeLabel: "03:50",
        body: "Click Assign Talks. Choose an unassigned talk and a couple or priest, then click Assign. Assigned talks are listed below. The trash icon removes one.",
        control: "Assign Talks",
        page: "/retreatRoster",
      },
      {
        id: "import-export",
        title: "Sample, Import, and Export",
        time: 265,
        timeLabel: "04:25",
        body: "Sample downloads the Excel template. Import brings couples in from that file. Export downloads the roster by retreatant or team, as Excel or PDF, plus the flock note and all-data files.",
        control: "Sample, Import, Export",
        page: "/retreatRoster",
      },
      {
        id: "add-couple",
        title: "Add a Couple",
        time: 295,
        timeLabel: "04:55",
        body: "Click the blue plus. Add a new couple and fill in the required names, emails, parish, and anniversary. Or choose Select Couple from existing Couple Data, review the details, and click Add to Retreat.",
        control: "Add a couple",
        page: "/retreatRoster",
      },
      {
        id: "edit-couple",
        title: "Search, Edit, or Remove",
        time: 365,
        timeLabel: "06:05",
        body: "Search the roster by name. The pencil opens Edit couple, where you can change contact details, the photo, and the retreat role, then click Save Changes. The trash icon asks you to confirm before a couple is removed.",
        control: "Search, Edit, Delete",
        page: "/retreatRoster",
      },
      {
        id: "emails",
        title: "Copy Emails",
        time: 448,
        timeLabel: "07:28",
        body: "Use the envelope on a row to copy that couple's emails. The email icons on the role chips open the list for that group, such as retreatant emails, where you can copy one address or copy all.",
        control: "Copy emails",
        page: "/retreatRoster",
      },
      {
        id: "documents",
        title: "Program and Directory",
        time: 500,
        timeLabel: "08:20",
        body: "At the bottom, Create Program and Create Directory open the weekend documents. You can also upload a finished program or directory. On the program, set the support teams and talks, then publish or preview. Return to the roster when you are done.",
        control: "Create Program, Create Directory",
        page: "/retreatRoster",
      },
    ],
  },
  {
    id: "activate-retreat",
    title: "Activate or Deactivate a Retreat",
    description:
      "Use the Active/Deactivate switch on a retreat row. Deactivate stops registration.",
    topic: "Retreats",
    lengthLabel: "01:30",
    ...sharedVideo,
    steps: [
      {
        id: "open-list",
        title: "Open the Retreat List",
        time: 0,
        timeLabel: "00:10",
        body: "Stay on Retreats. The Active/Deactivate column is the first column for admins.",
        control: "Column: Active/Deactivate",
        action: {
          label: "Open Retreats",
          to: "/manageRetreat",
          continueGuide: true,
        },
      },
      {
        id: "find-row",
        title: "Find the Retreat Row",
        time: 40,
        timeLabel: "00:40",
        body: "Search by parish, language, or location. You can also filter the list by parish.",
        control: "Search or Parish filter",
      },
      {
        id: "toggle",
        title: "Switch Active or Inactive",
        time: 90,
        timeLabel: "01:30",
        body: "Turn the switch on for Active. Turn it off to deactivate. Deactivate stops retreat registration. On an inactive row, the pencil edits the deactivate reason.",
        control: "Switch: Active or Inactive",
      },
    ],
  },
  {
    id: "what-is-roster",
    title: "What is Roster?",
    description:
      "The Roster is the complete list of couples on a Retreat, as Retreatants and as Team members.",
    topic: "Retreat Closures",
    lengthLabel: "02:13",
    videoSrc: "/training/what-is-roster.mp4",
    thumbnail: "/training/what-is-roster.png",
    durationSeconds: 133,
    narrated: true,
    definition: [
      "The Roster contains the complete list of couples participating in the Retreat, both as Retreatants and as Team members. It includes their details, pictures, and assigned roles and services, such as Lead, Co-Lead, Spiritual Mentor, Spiritual Director, Speakers, and Support Teams.",
      "Please ensure that all couples' details and pictures are complete and accurate to confirm the Roster data and proceed with closing the Retreat.",    ],
    steps: [
      {
        id: "definition",
        title: "What is Roster?",
        time: 0,
        timeLabel: "00:00",
        body: "The Roster contains the complete list of couples participating in the Retreat.",
        page: "/retreat-closure-list",
      },
    ],
  },
  {
    id: "what-is-directory",
    title: "What is Directory?",
    description:
      "The Directory is generated from Roster data and shows pictures, names, and contact information.",
    topic: "Retreat Closures",
    lengthLabel: "02:57",
    videoSrc: "/training/what-is-directory.mp4",
    thumbnail: "/training/what-is-directory.png",
    durationSeconds: 177,
    narrated: true,
    definition: [
      "The Directory is a document automatically generated from the Roster data. It displays the pictures, names, and contact information of all Leaders, Retreatants, and Team Couples participating in the Retreat.",
      "You can also add multiple group Pictures and customised wording at the end of the Directory. Once all the information is complete, you can preview and publish the Directory.",
      "After publishing, the system will automatically mark the Directory as Confirmed for Retreat Closure.",
    ],
    steps: [
      {
        id: "definition",
        title: "What is Directory?",
        time: 0,
        timeLabel: "00:00",
        body: "The Directory is a document automatically generated from the Roster data.",
        page: "/retreat-closure-list",
      },
    ],
  },
  {
    id: "what-is-pictures",
    title: "How to upload missing Pictures?",
    description:
      "The Pictures section holds photos of every couple on the Retreat, and confirms when they are complete.",
    topic: "Retreat Closures",
    lengthLabel: "01:35",
    videoSrc: "/training/what-is-pictures.mp4",
    thumbnail: "/training/what-is-pictures.png",
    durationSeconds: 95,
    narrated: true,
    definition: [
      "The pictures section contains photos of all couples participating in the Retreat, including Retreatants, Team couples, and Priests. A picture must be uploaded for every couple listed in the Roster.",
      "Once all the required pictures have been uploaded, the system will automatically mark the Pictures toggle as Confirmed for the Roster Closure process.",
    ],
    steps: [
      {
        id: "definition",
        title: "How to upload missing Pictures?",
        time: 0,
        timeLabel: "00:00",
        body: "The Pictures section contains photos of all couples participating in the Retreat.",
        page: "/retreat-closure-list",
      },
    ],
  },
  {
    id: "close-retreat",
    title: "Close a Retreat",
    description:
      "On Retreat Closures, confirm roster, directory, program, and pictures, then mark the retreat Validated & Closed.",
    topic: "Retreat Closures",
    lengthLabel: "01:20",
    ...sharedVideo,
    steps: [
      {
        id: "open-closures",
        title: "Open Retreat Closures",
        time: 0,
        timeLabel: "00:10",
        body: "Open Retreat Closures after the weekend. Search by Retreat ID if the list is long.",
        control: "Page: Retreat Closures",
        action: {
          label: "Open Closures",
          to: "/retreat-closure-list",
          continueGuide: true,
        },
      },
      {
        id: "start-close",
        title: "Start the Closure",
        time: 35,
        timeLabel: "00:35",
        body: "Check the confirmation chips on that row: Roster Data, Directory, Program, and Pictures. Each one should be valid before you close.",
        control: "Confirmations",
      },
      {
        id: "confirm-close",
        title: "Confirm It Is Closed",
        time: 80,
        timeLabel: "01:20",
        body: "When those checks pass, the row can show Validated & Closed. The retreat stays in history and is no longer an open weekend.",
        control: "Validated & Closed",
      },
    ],
  },
  {
    id: "manage-couples",
    title: "Activate or Deactivate Couples",
    description:
      "On Manage Couples, search for the couple and set Status to Active, Inactive, Deceased, or Divorced.",
    topic: "Couples",
    lengthLabel: "01:10",
    ...sharedVideo,
    steps: [
      {
        id: "open-couples",
        title: "Open Manage Couples",
        time: 0,
        timeLabel: "00:10",
        body: "Open Manage Couples from the admin menu.",
        control: "Page: Manage Couples",
        action: {
          label: "Open Couples",
          to: "/manageCouples",
          continueGuide: true,
        },
      },
      {
        id: "search-couple",
        title: "Search for the Couple",
        time: 30,
        timeLabel: "00:30",
        body: "Search by name or email so you change the right account.",
        control: "Search",
      },
      {
        id: "toggle-couple",
        title: "Activate or Deactivate",
        time: 70,
        timeLabel: "01:10",
        body: "Use the Status menu on that row. Active lets the couple sign in. Inactive blocks the account. Deceased and Divorced are the other statuses.",
        control: "Status: Active, Inactive, Deceased, or Divorced",
      },
    ],
  },
];

export const trainingSections = [
  {
    id: "retreat-closures",
    title: "Retreat Closures",
    icon: "mdi-check-circle-outline",
    guideIds: ["what-is-roster", "what-is-directory", "what-is-pictures"],
  },
  {
    id: "retreats",
    title: "Retreats",
    icon: "mdi-church",
    guideIds: ["create-edit-retreat", "activate-retreat"],
  },
  {
    id: "retreat-roster",
    title: "Retreat Roster",
    icon: "mdi-clipboard-text-outline",
    guideIds: ["retreat-roster"],
  },
  {
    id: "couples",
    title: "Couples",
    icon: "mdi-account-heart",
    guideIds: ["manage-couples"],
  },
];

export function getTrainingGuide(id) {
  return trainingGuides.find((guide) => guide.id === id) || null;
}

export function canSeeTraining(user) {
  if (!user) return false;
  return (
    user.userRole === "ADMIN" ||
    user.userRole === "SUBADMIN" ||
    user.committeeMember === true
  );
}

export function trainingDismissKey(userId, guideId) {
  return `lovestrong.training.dismissed.${userId || "admin"}.${guideId}`;
}

export function isGuideDismissed(userId, guideId) {
  try {
    return localStorage.getItem(trainingDismissKey(userId, guideId)) === "1";
  } catch (error) {
    return false;
  }
}

export function dismissGuide(userId, guideId) {
  try {
    localStorage.setItem(trainingDismissKey(userId, guideId), "1");
  } catch (error) {
    /* ignore private-mode storage failures */
  }
}

export function resolveStepTime(step, steps, duration) {
  const last = steps[steps.length - 1]?.time || 0;
  if (!Number.isFinite(duration) || duration + 0.75 >= last) {
    return step.time;
  }
  if (last <= 0) return 0;
  return (step.time / last) * Math.max(duration - 0.35, 0);
}

export function stepIndexForTime(steps, time, duration) {
  let index = 0;
  for (let i = 0; i < steps.length; i += 1) {
    if (time + 0.2 >= resolveStepTime(steps[i], steps, duration)) {
      index = i;
    }
  }
  return index;
}

export function trainingMediaSrc(src) {
  if (!src) return "";
  if (/^https?:/i.test(src)) return src;
  const base = process.env.PUBLIC_URL || "";
  return `${base}${src}`;
}
